import { useSyncExternalStore } from 'react';

/**
 * Detects if the current device is mobile based on screen width
 * @param breakpoint - Width in pixels to consider as mobile (default: 768)
 * @returns boolean indicating if device is mobile
 */
export function isMobileDevice(breakpoint: number = 768): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < breakpoint;
}

/**
 * Detects if the device has touch capability
 * @returns boolean indicating if device supports touch
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-expect-error - msMaxTouchPoints is a legacy IE/Edge property not present in modern Navigator typings
    navigator.msMaxTouchPoints > 0
  );
}

/**
 * Detects if the current device is an Apple device (iOS/iPadOS)
 * @returns boolean indicating if device is iOS/iPadOS
 */
export function isAppleDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
}

/**
 * UA-based mobile-platform detection (iOS/Android), matching the regex the
 * root-route store redirect in App.tsx uses. Deliberately UA-only — no
 * viewport/touch heuristics — because it answers a *routing* question, not a
 * layout one: mobile-web visitors are intentionally sent to the app stores,
 * so "play in your browser" CTAs must only render for the desktop UAs that
 * are allowed to stay on the web funnel. Keep the regex in sync with the
 * client-side store redirect in App.tsx.
 * @returns boolean indicating the browser reports an iOS/Android user agent
 */
export function isMobileWebUA(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod|android/.test(navigator.userAgent.toLowerCase());
}

/**
 * Detects if the current device is an iPad specifically
 * @returns boolean indicating if device is iPad
 */
export function isIPad(): boolean {
  if (typeof window === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();
  const width = window.innerWidth;
  const height = window.innerHeight;
  
  // Check for iPad in user agent (works in Safari and older iPadOS)
  if (/ipad/.test(userAgent)) return true;

  // iPadOS 13+ reports as MacIntel with touch in Safari, but may differ in Capacitor
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;

  // Desktop OSes are never iPads. Touchscreen Windows/ChromeOS laptops have
  // maxTouchPoints > 0 AND large windows, so without this short-circuit they
  // fell through to the dimension fallback below and silently got the iPad
  // layout branches (tablet card sizes, max-w-6xl board, no HandEvaluator).
  if (/windows|cros/.test(userAgent)) return false;

  // Fallback: Check screen dimensions - iPad typically has larger screens
  // iPad Air/Pro: 820x1180 or bigger, iPad mini (6th gen+): 744x1133
  // iPhone max: ~430x932, so anything 744+ wide with tablet height is likely iPad
  // ONLY for touch devices — without the maxTouchPoints gate every desktop
  // browser window (e.g. 1920x1080, touch points 0) matched the landscape
  // check below, so isIPad()/isTablet() returned true on desktop web and all
  // the `!isIPad && isWeb` board styles were dead code.
  // ALSO gated on a coarse primary pointer — bare maxTouchPoints matched any
  // touch-capable device (e.g. touch-digitizer laptops) whose primary input
  // is still a mouse/trackpad. If the pointer query is unsupported the
  // fallback is simply skipped (the UA/platform checks above already cover
  // every real iPad configuration).
  // 744 (not 768): iPad mini portrait is 744x1133 — a 768 floor made the
  // mini fall back to the phone layout (UI audit P1-1, StackedStreak-zak3).
  // Largest phones are ~430pt wide, so 744 keeps a comfortable gap.
  const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  if (navigator.maxTouchPoints > 0 && coarsePointer) {
    if (width >= 744 && height >= 1024) return true;
    if (width >= 1024 && height >= 744) return true; // Landscape
  }

  return false;
}

/* ------------------------------------------------------------------------
 * Shared resize store for all device-detection hooks.
 *
 * Previously every hook instance registered its own `window` resize
 * listener — with 30 <Card>s each calling useIsMobile/useIsTouchDevice/
 * useIsTablet, that meant ~90 live listeners all doing the same work.
 * Now a single module-level listener fans out to every subscribed hook
 * via useSyncExternalStore, and it's only attached while at least one
 * component is subscribed.
 *
 * Snapshots are computed synchronously on first render (no more
 * useState(false) + useEffect correction pass), which also removes the
 * double initial render every consumer used to pay.
 * ---------------------------------------------------------------------- */

type Listener = () => void;
const resizeListeners = new Set<Listener>();
let resizeListenerAttached = false;

function notifyResizeListeners(): void {
  resizeListeners.forEach((listener) => listener());
}

function subscribeToResize(listener: Listener): () => void {
  resizeListeners.add(listener);
  if (!resizeListenerAttached && typeof window !== 'undefined') {
    window.addEventListener('resize', notifyResizeListeners);
    resizeListenerAttached = true;
  }
  return () => {
    resizeListeners.delete(listener);
    if (resizeListeners.size === 0 && resizeListenerAttached) {
      window.removeEventListener('resize', notifyResizeListeners);
      resizeListenerAttached = false;
    }
  };
}

// Server snapshot: all detection functions already return false when
// `window` is undefined, matching the previous hooks' initial state.
const getServerSnapshot = () => false;

/**
 * React hook to detect mobile devices with responsive updates
 * @param breakpoint - Width in pixels to consider as mobile (default: 768)
 * @returns boolean indicating if device is mobile
 */
export function useIsMobile(breakpoint: number = 768): boolean {
  return useSyncExternalStore(
    subscribeToResize,
    () => isMobileDevice(breakpoint),
    getServerSnapshot
  );
}

/**
 * React hook to detect touch devices
 * @returns boolean indicating if device supports touch
 */
export function useIsTouchDevice(): boolean {
  // Touch capability is static, but subscribing to the shared store keeps
  // the hook contract (re-evaluated on resize) without any extra listener.
  return useSyncExternalStore(subscribeToResize, isTouchDevice, getServerSnapshot);
}

/**
 * React hook to detect Apple devices
 * @returns boolean indicating if device is iOS/iPadOS
 */
export function useIsAppleDevice(): boolean {
  return useSyncExternalStore(subscribeToResize, isAppleDevice, getServerSnapshot);
}

/**
 * React hook to detect iPad devices
 * @returns boolean indicating if device is iPad
 */
export function useIsIPad(): boolean {
  return useSyncExternalStore(subscribeToResize, isIPad, getServerSnapshot);
}

/**
 * Detects if the current device is a tablet (iPad or 744px-1024px screen)
 * @returns boolean indicating if device is a tablet
 */
export function isTablet(): boolean {
  if (typeof window === 'undefined') return false;
  const width = window.innerWidth;
  // Check if iPad OR a TOUCH device whose screen size is in the tablet range
  // (744px - 1024px; 744 not 768 so iPad-mini-class widths qualify — see
  // isIPad above). The maxTouchPoints gate keeps narrow desktop browser
  // windows from being misdetected as tablets (same fix as isIPad above).
  return (
    isIPad() ||
    (navigator.maxTouchPoints > 0 && width >= 744 && width <= 1024)
  );
}

/**
 * React hook to detect tablet devices
 * @returns boolean indicating if device is a tablet
 */
export function useIsTablet(): boolean {
  return useSyncExternalStore(subscribeToResize, isTablet, getServerSnapshot);
}

/**
 * Hook that combines mobile and touch detection
 * @returns object with mobile and touch detection flags
 */
export function useDeviceCapabilities() {
  const isMobile = useIsMobile();
  const isTouch = useIsTouchDevice();
  const isTabletDevice = useIsTablet();

  return {
    isMobile,
    isTouch,
    isTablet: isTabletDevice,
    isDesktop: !isMobile && !isTabletDevice,
    shouldReduceMotion: isMobile || (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches),
  };
}

